'use client';

import { useEffect } from 'react';

/**
 * Route-segment error boundary (App Router). Catches anything thrown while
 * rendering a page/layout below `app/` — server components included.
 *
 * In production Next.js strips the message and replaces it with a `digest`
 * hash; the matching full stack is in the server log for that digest. In dev we
 * show message + stack inline so a failure is diagnosable without leaving the
 * browser.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): React.JSX.Element {
  const isDev = process.env.NODE_ENV !== 'production';

  useEffect(() => {
    // Surface in the browser console too — the overlay hides on navigation.
    console.error('[web] render error', { digest: error.digest, error });
  }, [error]);

  return (
    <div
      style={{
        padding: '2rem',
        margin: '2rem auto',
        maxWidth: 900,
        background: 'var(--surface-1)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        color: 'var(--text-primary)',
      }}
    >
      <h2 style={{ margin: '0 0 0.5rem' }}>Something broke while rendering this page.</h2>
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 1rem' }}>
        {isDev ? error.message : 'The details were logged on the server.'}
      </p>

      {error.digest && (
        <p style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 13 }}>
          digest: {error.digest} — grep the API/web server log for this to find the full stack.
        </p>
      )}

      {isDev && error.stack && (
        <pre
          style={{
            overflowX: 'auto',
            padding: '1rem',
            background: 'var(--plane)',
            border: '1px solid var(--grid)',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.5,
            color: 'var(--text-secondary)',
          }}
        >
          {error.stack}
        </pre>
      )}

      <button
        onClick={reset}
        style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'var(--series-1)',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}
